import { getToken } from "firebase/messaging";
import { getInstallations, getToken as getInstallationToken } from "firebase/installations";

const messagingFailures = new Set([
  "messaging/token-subscribe-failed",
  "messaging/token-update-failed",
  "messaging/token-unsubscribe-failed",
]);

function rejectedInstallation(error) {
  return error?.code === "installations/request-failed" &&
    [401, 404].includes(Number(error.customData?.serverCode));
}

function rejectedMessagingCredential(error) {
  return messagingFailures.has(error?.code) &&
    /\b401\b|UNAUTHENTICATED|FIS_AUTH_ERROR|missing required authentication credential/i.test(
      error.customData?.errorInfo || error.message || ""
    );
}

async function replaceSubscriptionForOldKey(options) {
  const manager = options.serviceWorkerRegistration?.pushManager;
  if (!manager || !options.vapidKey) return;
  const subscription = await manager.getSubscription();
  const currentKey = subscription?.options?.applicationServerKey;
  if (!currentKey) return;
  const encoded = options.vapidKey.replace(/-/g, "+").replace(/_/g, "/");
  const expected = Uint8Array.from(atob(encoded + "=".repeat((4 - encoded.length % 4) % 4)), c => c.charCodeAt(0));
  const current = new Uint8Array(currentKey);
  if (current.length === expected.length && current.every((value, index) => value === expected[index])) return;
  // The Firebase SDK reuses an existing PushSubscription even when getToken
  // receives a different VAPID key. Remove only that mismatched subscription;
  // getToken below creates its replacement with the configured public key.
  await subscription.unsubscribe();
}

export async function getWebPushToken(app, messaging, options) {
  await replaceSubscriptionForOldKey(options);
  try {
    return await getToken(messaging, options);
  } catch (error) {
    if (!rejectedMessagingCredential(error) && !rejectedInstallation(error)) throw error;
    const installations = getInstallations(app);
    try {
      // FCM can reject an installation token that the SDK still considers fresh.
      await getInstallationToken(installations, true);
    } catch (refreshError) {
      if (!rejectedInstallation(refreshError)) throw refreshError;
      // The Installations SDK removes its cached entry after a 401/404.
      // Let it create a replacement; don't clear browser storage or login state.
      await getInstallationToken(installations, true);
    }
    // One retry only. Configuration, permission and network failures surface.
    return getToken(messaging, options);
  }
}
